import React from 'react';
import {
  FaCompass,
  FaFlask,
  FaCodeBranch,
  FaPenNib,
  FaCertificate,
  FaMicrophone,
  FaCoffee,
  FaArrowRight
} from 'react-icons/fa';
import Section from './layout/Section';
import ScrollReveal from './animations/ScrollReveal';
import SpotlightCard from './effects/SpotlightCard';
import { beyond } from '../data/profile';

const Card = ({ icon: Icon, title, children, className = '', delay = 0 }) => (
  <ScrollReveal direction="up" delay={delay} className="h-full">
    <SpotlightCard className={`h-full p-6 ${className}`}>
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500"
        >
          <Icon className="h-3.5 w-3.5" />
        </span>
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-900 dark:text-white">
          {title}
        </h3>
      </div>
      <div className="mt-5">{children}</div>
    </SpotlightCard>
  </ScrollReveal>
);

export default function Beyond() {
  return (
    <Section
      id="beyond"
      eyebrow="Beyond the code"
      title="What I’m into right now"
      lead="Learning, writing, and side work around the products I build."
      className="bg-slate-50 dark:bg-slate-900"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-dotted opacity-40" />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card icon={FaCompass} title="Current focus">
          <ul className="space-y-3">
            {beyond.currentFocus.map((focus) => (
              <li key={focus} className="flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-blue-500"
                />
                <span className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{focus}</span>
              </li>
            ))}
          </ul>
        </Card>

        <Card icon={FaFlask} title="Exploring" delay={0.05}>
          <ul className="flex flex-wrap gap-1.5">
            {beyond.exploring.map((item) => (
              <li
                key={item}
                className="rounded-full border border-dashed border-slate-300 px-2.5 py-1 text-[11.5px]
                           font-medium text-slate-600 dark:border-white/[0.12] dark:text-slate-400"
              >
                {item}
              </li>
            ))}
          </ul>
        </Card>

        <Card icon={FaCodeBranch} title="Open source" delay={0.1}>
          <ul className="space-y-4">
            {beyond.openSource.map((repo) => (
              <li key={repo.name}>
                <a
                  href={repo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group/link inline-flex items-center gap-1.5 text-sm font-semibold
                             text-slate-900 hover:text-blue-600 dark:text-white dark:hover:text-blue-400"
                >
                  {repo.name}
                  <FaArrowRight
                    className="h-2.5 w-2.5 -rotate-45 transition-transform group-hover/link:translate-x-0.5"
                    aria-hidden="true"
                  />
                </a>
                <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {repo.description}
                </p>
              </li>
            ))}
          </ul>
        </Card>

        <Card icon={FaPenNib} title="Writing" delay={0.05}>
          <ul className="space-y-3.5">
            {beyond.writing.map((post) => (
              <li key={post.title} className="flex items-start justify-between gap-3">
                <span className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {post.title}
                </span>
                <span
                  className="mt-0.5 flex-none rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium
                             uppercase tracking-wider text-slate-500 dark:bg-white/[0.06] dark:text-slate-400"
                >
                  {post.status}
                </span>
              </li>
            ))}
          </ul>
        </Card>

        <Card icon={FaCertificate} title="Certifications" delay={0.1}>
          <ul className="space-y-3.5">
            {beyond.certifications.map((cert) => (
              <li key={cert.name}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{cert.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">{cert.issuer}</p>
              </li>
            ))}
          </ul>
        </Card>

        <Card icon={FaMicrophone} title="Speaking" delay={0.15}>
          <ul className="space-y-3.5">
            {beyond.speaking.map((talk) => (
              <li key={talk.title}>
                <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{talk.title}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {talk.event} &middot; {talk.year}
                </p>
              </li>
            ))}
          </ul>
        </Card>

        <Card
          icon={FaCoffee}
          title="Fun facts"
          delay={0.1}
          className="md:col-span-2 lg:col-span-3"
        >
          <ul className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {beyond.funFacts.map((fact) => (
              <li key={fact} className="flex items-start gap-2.5">
                <span aria-hidden="true" className="mt-[7px] h-1.5 w-1.5 flex-none rounded-full bg-slate-400" />
                <span className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">{fact}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </Section>
  );
}
